import * as React from 'react';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { Check, ChevronRight, Circle } from 'lucide-react';

import { cn } from '../utils';
import { Slot } from './common';

/* -------------------------------------------------------------------------------------------------
 * DropdownMenu
 * -----------------------------------------------------------------------------------------------*/

const UiDropdownMenu = DropdownMenuPrimitive.Root;

const UiDropdownMenuTrigger = DropdownMenuPrimitive.Trigger;

const UiDropdownMenuGroup = DropdownMenuPrimitive.Group;

const UiDropdownMenuPortal = DropdownMenuPrimitive.Portal;

const UiDropdownMenuSub = DropdownMenuPrimitive.Sub;

const UiDropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup;

/* -------------------------------------------------------------------------------------------------
 * DropdownMenu / SubTrigger
 * -----------------------------------------------------------------------------------------------*/

const UiDropdownMenuSubTrigger = React.forwardRef<
	React.ElementRef<typeof DropdownMenuPrimitive.SubTrigger>,
	React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubTrigger> & {
		inset?: boolean;
	}
>(({ className, inset, children, ...props }, ref) => (
	<DropdownMenuPrimitive.SubTrigger
		ref={ref}
		className={cn(
			'flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none',
			'focus:bg-zinc-100 data-[state=open]:bg-zinc-100',
			'dark:focus:bg-zinc-800 dark:data-[state=open]:bg-zinc-800',
			inset && 'pl-8',
			className,
		)}
		{...props}
	>
		{children}
		<ChevronRight className="ml-auto size-4" />
	</DropdownMenuPrimitive.SubTrigger>
));
UiDropdownMenuSubTrigger.displayName = DropdownMenuPrimitive.SubTrigger.displayName;

/* -------------------------------------------------------------------------------------------------
 * DropdownMenu / SubContent
 * -----------------------------------------------------------------------------------------------*/

const UiDropdownMenuSubContent = React.forwardRef<
	React.ElementRef<typeof DropdownMenuPrimitive.SubContent>,
	React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubContent>
>(({ className, ...props }, ref) => (
	<DropdownMenuPrimitive.SubContent
		ref={ref}
		className={cn(
			'z-[211] min-w-[8rem] overflow-hidden rounded-md border p-1 shadow-lg',
			'border-zinc-200 bg-white text-zinc-950',
			'dark:border-zinc-800 dark:bg-zinc-950 dark:text-zinc-50',
			'data-[state=open]:animate-in data-[state=closed]:animate-out',
			'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
			'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
			'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2',
			'data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
			className,
		)}
		{...props}
	/>
));
UiDropdownMenuSubContent.displayName = DropdownMenuPrimitive.SubContent.displayName;

/* -------------------------------------------------------------------------------------------------
 * DropdownMenu / Content
 * -----------------------------------------------------------------------------------------------*/

const UiDropdownMenuContent = React.forwardRef<
	React.ElementRef<typeof DropdownMenuPrimitive.Content>,
	React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
	<DropdownMenuPrimitive.Portal>
		<DropdownMenuPrimitive.Content
			ref={ref}
			sideOffset={sideOffset}
			className={cn(
				'z-[211] min-w-[175px] overflow-hidden rounded-md border p-1 shadow-md',
				'border-zinc-200 bg-white text-zinc-950',
				'dark:border-zinc-800 dark:bg-zinc-950 dark:text-zinc-50',
				'data-[state=open]:animate-in data-[state=closed]:animate-out',
				'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
				'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
				'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2',
				'data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
				className,
			)}
			{...props}
		/>
	</DropdownMenuPrimitive.Portal>
));
UiDropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName;

/* -------------------------------------------------------------------------------------------------
 * DropdownMenu / Item
 * -----------------------------------------------------------------------------------------------*/

const UiDropdownMenuItem = React.forwardRef<
	React.ElementRef<typeof DropdownMenuPrimitive.Item>,
	React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> & {
		inset?: boolean;
	}
>(({ className, inset, ...props }, ref) => (
	<DropdownMenuPrimitive.Item
		ref={ref}
		className={cn(
			'relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors',
			'focus:bg-zinc-100 focus:text-zinc-900',
			'dark:focus:bg-zinc-800 dark:focus:text-zinc-50',
			'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
			'data-[selected=true]:bg-zinc-100',
			'dark:data-[selected=true]:bg-zinc-800',
			inset && 'pl-8',
			className,
		)}
		{...props}
	/>
));
UiDropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName;

/* -------------------------------------------------------------------------------------------------
 * DropdownMenu / CheckboxItem
 * -----------------------------------------------------------------------------------------------*/

const UiDropdownMenuCheckboxItem = React.forwardRef<
	React.ElementRef<typeof DropdownMenuPrimitive.CheckboxItem>,
	React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.CheckboxItem>
>(({ className, children, checked, ...props }, ref) => (
	<DropdownMenuPrimitive.CheckboxItem
		ref={ref}
		className={cn(
			'relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none transition-colors',
			'focus:bg-zinc-100 focus:text-zinc-900',
			'dark:focus:bg-zinc-800 dark:focus:text-zinc-50',
			'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
			className,
		)}
		checked={checked}
		{...props}
	>
		<span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
			<DropdownMenuPrimitive.ItemIndicator>
				<Check className="size-4" />
			</DropdownMenuPrimitive.ItemIndicator>
		</span>
		{children}
	</DropdownMenuPrimitive.CheckboxItem>
));
UiDropdownMenuCheckboxItem.displayName = DropdownMenuPrimitive.CheckboxItem.displayName;

/* -------------------------------------------------------------------------------------------------
 * DropdownMenu / RadioItem
 * -----------------------------------------------------------------------------------------------*/

const UiDropdownMenuRadioItem = React.forwardRef<
	React.ElementRef<typeof DropdownMenuPrimitive.RadioItem>,
	React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.RadioItem>
>(({ className, children, ...props }, ref) => (
	<DropdownMenuPrimitive.RadioItem
		ref={ref}
		className={cn(
			'relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none transition-colors',
			'focus:bg-zinc-100 focus:text-zinc-900',
			'dark:focus:bg-zinc-800 dark:focus:text-zinc-50',
			'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
			className,
		)}
		{...props}
	>
		<span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
			<DropdownMenuPrimitive.ItemIndicator>
				<Circle className="size-2 fill-current" />
			</DropdownMenuPrimitive.ItemIndicator>
		</span>
		{children}
	</DropdownMenuPrimitive.RadioItem>
));
UiDropdownMenuRadioItem.displayName = DropdownMenuPrimitive.RadioItem.displayName;

/* -------------------------------------------------------------------------------------------------
 * DropdownMenu / ItemIcon
 * -----------------------------------------------------------------------------------------------*/

const UiDropdownMenuItemIcon = React.forwardRef<React.ElementRef<typeof Slot>, React.ComponentPropsWithoutRef<typeof Slot>>(
	({ className, children, ...props }, ref) => (
		<Slot className={cn('size-4 shrink-0 mr-2', className)} {...props}>
			{children}
		</Slot>
	),
);
UiDropdownMenuItemIcon.displayName = 'UiDropdownMenuItemIcon';

/* -------------------------------------------------------------------------------------------------
 * DropdownMenu / Label
 * -----------------------------------------------------------------------------------------------*/

const UiDropdownMenuLabel = React.forwardRef<
	React.ElementRef<typeof DropdownMenuPrimitive.Label>,
	React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label> & {
		inset?: boolean;
	}
>(({ className, inset, ...props }, ref) => (
	<DropdownMenuPrimitive.Label ref={ref} className={cn('px-2 py-1.5 text-sm font-semibold', inset && 'pl-8', className)} {...props} />
));
UiDropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName;

/* -------------------------------------------------------------------------------------------------
 * DropdownMenu / Separator
 * -----------------------------------------------------------------------------------------------*/

const UiDropdownMenuSeparator = React.forwardRef<
	React.ElementRef<typeof DropdownMenuPrimitive.Separator>,
	React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>
>(({ className, ...props }, ref) => (
	<DropdownMenuPrimitive.Separator ref={ref} className={cn('-mx-1 my-1 h-px', 'bg-zinc-100', 'dark:bg-zinc-800', className)} {...props} />
));
UiDropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName;

/* -------------------------------------------------------------------------------------------------
 * DropdownMenu / Shortcut
 * -----------------------------------------------------------------------------------------------*/

const UiDropdownMenuShortcut = ({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) => (
	<span className={cn('ml-auto text-xs tracking-widest opacity-60', className)} {...props} />
);
UiDropdownMenuShortcut.displayName = 'UiDropdownMenuShortcut';

/* -----------------------------------------------------------------------------------------------*/

export {
	UiDropdownMenu,
	UiDropdownMenuTrigger,
	UiDropdownMenuContent,
	UiDropdownMenuItem,
	UiDropdownMenuItemIcon,
	UiDropdownMenuCheckboxItem,
	UiDropdownMenuRadioItem,
	UiDropdownMenuLabel,
	UiDropdownMenuSeparator,
	UiDropdownMenuShortcut,
	UiDropdownMenuGroup,
	UiDropdownMenuPortal,
	UiDropdownMenuSub,
	UiDropdownMenuSubContent,
	UiDropdownMenuSubTrigger,
	UiDropdownMenuRadioGroup,
};
